const API_BASE = process.env.NEXT_PUBLIC_API_URL ?? "";

async function fetchApi<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...options,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail || `API error: ${res.status}`);
  }
  return res.json();
}

export async function login(
  username: string,
  password: string
): Promise<{ user: import("./types").UserInfo }> {
  return fetchApi("/api/auth/login", {
    method: "POST",
    body: JSON.stringify({ username, password }),
  });
}

export async function logout(): Promise<void> {
  await fetchApi("/api/auth/logout", { method: "POST" });
}

export async function getMe(): Promise<{
  user: import("./types").UserInfo;
}> {
  return fetchApi("/api/auth/me");
}

export async function getDashboard(): Promise<
  import("./types").DashboardData
> {
  return fetchApi("/api/dashboard");
}

export async function getFacilities(): Promise<{
  facilities: import("./types").Facility[];
}> {
  return fetchApi("/api/facilities");
}

export async function getFacility(
  applicationNo: string
): Promise<{ facility: import("./types").Facility }> {
  return fetchApi(`/api/facilities/${encodeURIComponent(applicationNo)}`);
}

export async function getEquivalentQuantities(): Promise<{
  equivalentQuantities: import("./types").EquivalentQuantity[];
}> {
  return fetchApi("/api/equivalent-quantities");
}

export async function getEquivalentQuantity(
  applicationNo: string
): Promise<{ equivalentQuantity: import("./types").EquivalentQuantity }> {
  return fetchApi(
    `/api/equivalent-quantities/${encodeURIComponent(applicationNo)}`
  );
}

export async function getCertificates(): Promise<{
  certificates: import("./types").Certificate[];
}> {
  return fetchApi("/api/certificates");
}

export async function getCertificate(
  certificateId: string
): Promise<{ certificate: import("./types").Certificate }> {
  return fetchApi(`/api/certificates/${encodeURIComponent(certificateId)}`);
}

export async function getOrganizations(): Promise<{
  organizations: { id: string; code: string; name: string; orgType: string }[];
}> {
  return fetchApi("/api/organizations");
}

export async function createExercise(body: {
  certificate_id: string;
  quantity_mj: number;
  purpose: string;
}): Promise<{ exercise: import("./types").ExerciseRecord }> {
  return fetchApi("/api/exercises", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function createTransfer(body: {
  certificate_id: string;
  quantity_mj: number;
  to_organization_id: string;
}): Promise<{
  transfer: {
    fromCertificateId: string;
    toCertificateId: string;
    quantityMj: number;
    toOrganization: string;
  };
}> {
  return fetchApi("/api/transfers", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function getAdminOrganizations(): Promise<{
  organizations: import("./types").OrganizationRecord[];
}> {
  return fetchApi("/api/admin/organizations");
}

export async function getAdminUsers(): Promise<{
  users: import("./types").UserRecord[];
}> {
  return fetchApi("/api/admin/users");
}

export async function getAuditLogs(
  limit = 100,
  offset = 0
): Promise<{
  auditLogs: import("./types").AuditLogRecord[];
}> {
  return fetchApi(`/api/admin/audit-logs?limit=${limit}&offset=${offset}`);
}

export function getExportUrl(exportType: string): string {
  return `${API_BASE}/api/export/${exportType}?format=csv`;
}

// --- 設備認定 申請・承認 ---

export async function applyFacility(body: {
  gas_type: string;
  facility_name: string;
  address: string;
  production_capacity?: string;
  manufacturer_name: string;
  operation_start_date?: string;
  remarks?: string;
}): Promise<{ facility: import("./types").Facility }> {
  return fetchApi("/api/facilities/apply", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function approveFacility(
  applicationNo: string,
  body: { certification_number: string; certification_date: string }
): Promise<{ facility: import("./types").Facility }> {
  return fetchApi(
    `/api/facilities/${encodeURIComponent(applicationNo)}/approve`,
    { method: "POST", body: JSON.stringify(body) }
  );
}

export async function rejectFacility(
  applicationNo: string,
  body: { reason: string }
): Promise<{ facility: import("./types").Facility }> {
  return fetchApi(
    `/api/facilities/${encodeURIComponent(applicationNo)}/reject`,
    { method: "POST", body: JSON.stringify(body) }
  );
}

// --- 相当量認証 申請・承認 ---

export async function applyEquivalentQuantity(body: {
  facility_cert_number: string;
  h2_producer_code?: string;
  h2_producer_name?: string;
  co2_producer_code?: string;
  co2_producer_name?: string;
  period_start: string;
  period_end: string;
  quantity_mj: number;
}): Promise<{ equivalentQuantity: import("./types").EquivalentQuantity }> {
  return fetchApi("/api/equivalent-quantities/apply", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function approveEquivalentQuantity(
  applicationNo: string,
  body: { certification_date: string; serial_number: string }
): Promise<{ equivalentQuantity: import("./types").EquivalentQuantity }> {
  return fetchApi(
    `/api/equivalent-quantities/${encodeURIComponent(applicationNo)}/approve`,
    { method: "POST", body: JSON.stringify(body) }
  );
}

export async function rejectEquivalentQuantity(
  applicationNo: string,
  body: { reason: string }
): Promise<{ equivalentQuantity: import("./types").EquivalentQuantity }> {
  return fetchApi(
    `/api/equivalent-quantities/${encodeURIComponent(applicationNo)}/reject`,
    { method: "POST", body: JSON.stringify(body) }
  );
}

// --- 証書発行 申請・承認 ---

export async function applyCertificate(body: {
  eq_application_no: string;
  quantity_mj: number;
  purpose?: string;
  exercise_period?: string;
}): Promise<{ certificate: import("./types").Certificate }> {
  return fetchApi("/api/certificates/apply", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function approveCertificate(
  certificateId: string
): Promise<{ certificate: import("./types").Certificate }> {
  return fetchApi(
    `/api/certificates/${encodeURIComponent(certificateId)}/approve`,
    { method: "POST" }
  );
}

export async function rejectCertificate(
  certificateId: string,
  body: { reason: string }
): Promise<{ certificate: import("./types").Certificate }> {
  return fetchApi(
    `/api/certificates/${encodeURIComponent(certificateId)}/reject`,
    { method: "POST", body: JSON.stringify(body) }
  );
}

// --- 廃止 ---

export async function revokeFacility(
  applicationNo: string
): Promise<{ facility: import("./types").Facility }> {
  return fetchApi(
    `/api/facilities/${encodeURIComponent(applicationNo)}/revoke`,
    { method: "POST" }
  );
}

export async function revokeEquivalentQuantity(
  applicationNo: string
): Promise<{ equivalentQuantity: import("./types").EquivalentQuantity }> {
  return fetchApi(
    `/api/equivalent-quantities/${encodeURIComponent(applicationNo)}/revoke`,
    { method: "POST" }
  );
}

// --- 一括入力 ---

export async function bulkApplyFacilities(
  body: {
    gas_type: string;
    facility_name: string;
    address: string;
    manufacturer_name: string;
    production_capacity?: string;
  }[]
): Promise<{ facilities: import("./types").Facility[]; count: number }> {
  return fetchApi("/api/facilities/bulk-apply", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function bulkApplyEquivalentQuantities(
  body: {
    facility_cert_number: string;
    period_start: string;
    period_end: string;
    quantity_mj: number;
    h2_producer_code?: string;
    h2_producer_name?: string;
    co2_producer_code?: string;
    co2_producer_name?: string;
  }[]
): Promise<{
  equivalentQuantities: import("./types").EquivalentQuantity[];
  count: number;
}> {
  return fetchApi("/api/equivalent-quantities/bulk-apply", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// --- レポート ---

export async function getReportCertificate(
  certificateId: string
): Promise<{ certificate: import("./types").Certificate }> {
  return fetchApi(
    `/api/reports/public-document/${encodeURIComponent(certificateId)}`
  );
}

export async function getTransactionHistory(
  startDate?: string,
  endDate?: string
): Promise<{ transactions: import("./types").TransactionRecord[] }> {
  const params = new URLSearchParams();
  if (startDate) params.set("start_date", startDate);
  if (endDate) params.set("end_date", endDate);
  const qs = params.toString();
  return fetchApi(`/api/reports/transaction-history${qs ? `?${qs}` : ""}`);
}

export function getTransactionHistoryCsvUrl(
  startDate?: string,
  endDate?: string
): string {
  const params = new URLSearchParams({ format: "csv" });
  if (startDate) params.set("start_date", startDate);
  if (endDate) params.set("end_date", endDate);
  return `${API_BASE}/api/reports/transaction-history?${params.toString()}`;
}

// --- APIアカウント ---

export async function getApiAccounts(): Promise<{
  apiAccounts: import("./types").ApiAccountRecord[];
}> {
  return fetchApi("/api/api-accounts");
}

export async function createApiAccount(body: {
  name: string;
  organization_id: string;
}): Promise<{ apiAccount: import("./types").ApiAccountRecord }> {
  return fetchApi("/api/api-accounts", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function revokeApiAccount(
  accountId: string
): Promise<{ apiAccount: import("./types").ApiAccountRecord }> {
  return fetchApi(
    `/api/api-accounts/${encodeURIComponent(accountId)}/revoke`,
    { method: "POST" }
  );
}

// --- 文書管理 ---

export async function getDocuments(
  resourceType: string,
  resourceId: string
): Promise<{ documents: import("./types").DocumentRecord[] }> {
  return fetchApi(
    `/api/documents?resource_type=${encodeURIComponent(resourceType)}&resource_id=${encodeURIComponent(resourceId)}`
  );
}

export async function createDocument(body: {
  resource_type: string;
  resource_id: string;
  title: string;
  doc_type: string;
  description?: string;
}): Promise<{ document: import("./types").DocumentRecord }> {
  return fetchApi("/api/documents", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function deleteDocument(documentId: string): Promise<void> {
  await fetchApi(`/api/documents/${encodeURIComponent(documentId)}`, {
    method: "DELETE",
  });
}

// --- データリセット ---

export async function resetData(): Promise<{ message: string }> {
  return fetchApi("/api/admin/reset-data", { method: "POST" });
}
